import { Outlet, useParams } from '@tanstack/react-router';
import { WorkspaceShell } from '@/components/WorkspaceShell';
import { TopProgressBar } from '@/components/TopProgressBar';

/**
 * Stable workspace shell + content `<Outlet />`. Lives at the
 * `/w/$workspaceSlug` parent route so every in-app surface
 * (project list, board, calendar, notes, settings, etc.) renders
 * INSIDE the same persistent sidebar + header instance.
 *
 * Why: prior to slice 9d-bis, every page imported and rendered
 * `<WorkspaceShell>` itself. Navigating between sibling pages
 * unmounted and remounted the shell on every transition, producing
 * the "full-page reload" feel even with code-splitting + loaders.
 * Hoisting the shell into a parent layout route makes navigation
 * swap only the `<Outlet />` content — the sidebar, project list,
 * footer nav, header bar, and notification bell stay painted across
 * every workspace navigation. That's the primary mechanism behind
 * the SPA feel.
 *
 * No `<Suspense>` boundary around `<Outlet />` because page
 * components are now eagerly imported in the router (see comment
 * there for the full rationale). With no `lazy()`, there's nothing
 * for Suspense to catch — the content area swaps synchronously when
 * the loader resolves, with data already in the cache.
 *
 * `<TopProgressBar />` lives here so its 2px indicator is visible
 * across every in-app navigation without each route having to
 * remember to render it.
 */
export const WorkspaceLayout = () => {
  const { workspaceSlug } = useParams({ from: '/w/$workspaceSlug' });
  return (
    <>
      <TopProgressBar />
      <WorkspaceShell workspaceSlug={workspaceSlug}>
        <Outlet />
      </WorkspaceShell>
    </>
  );
};
